import { WindowOrder } from '@cutr/constants/windows/windows';
import { useTranslation } from 'react-i18next';

import { InfoMessage } from '@/blocks/InfoMessage';
import { Button } from '@/primitives/Button';
import { Icon, Send } from '@/primitives/Icons';

import { useRefreshOrder, useSendQuote } from '../queries/agent/queries';

const SyncPricingAndImages = ({ order }: { order: WindowOrder }) => {
  const { t } = useTranslation();
  const {
    mutateAsync: refreshOrder,
    isPending,
    isError,
    error,
  } = useRefreshOrder(order.id);

  return (
    <>
      <Button
        icon={<Icon icon={<Send />} />}
        onClick={() => {
          refreshOrder(order.id);
        }}
        className="outline"
        disabled={isPending}
      >
        {t('agent.quoteSummary.cta')}
      </Button>
      {isError && (
        <InfoMessage
          variant="error"
          message={t('agent.quoteSummary.syncError', {
            error: error?.message,
          })}
        />
      )}
    </>
  );
};

const quoteSentAtIsUpToDate = (order: WindowOrder) => {
  if (!order.quoteReadyAt) return false;

  if (!order.quoteSentAt) return false;

  return new Date(order.quoteSentAt) >= new Date(order.quoteReadyAt);
};

const QuoteReadyFooter = ({ order }: { order: WindowOrder }) => {
  const { t } = useTranslation();
  const {
    mutateAsync: sendQuote,
    isPending: isSendPending,
    isError: isSendError,
    error: sendError,
  } = useSendQuote(order.id);

  const {
    mutateAsync: refreshOrder,
    isPending: isRefreshPending,
    isError: isRefreshError,
    error: refreshError,
  } = useRefreshOrder(order.id);

  const alreadySent = quoteSentAtIsUpToDate(order);

  return (
    <div className="stack">
      <Button
        icon={<Icon icon={<Send />} />}
        onClick={() => sendQuote()}
        className={alreadySent ? 'outline' : undefined}
        disabled={isSendPending || isRefreshPending}
      >
        {alreadySent
          ? t('agent.quoteSummary.resendQuote')
          : t('agent.quoteSummary.sendQuote')}
      </Button>
      <Button
        icon={<Icon icon={<Send />} />}
        onClick={() => refreshOrder(order.id)}
        className="outline"
        disabled={isSendPending || isRefreshPending}
      >
        {t('agent.quoteSummary.cta')}
      </Button>
      {isSendError && (
        <InfoMessage
          variant="error"
          message={t('agent.quoteSummary.sendQuoteError', {
            error: sendError?.message,
          })}
        />
      )}
      {isRefreshError && (
        <InfoMessage
          variant="error"
          message={t('agent.quoteSummary.syncError', {
            error: refreshError?.message,
          })}
        />
      )}
    </div>
  );
};

const FooterForStatus: Record<
  WindowOrder['status'],
  null | React.ComponentType<{ order: WindowOrder }>
> = {
  draft: null,
  quote_requested: SyncPricingAndImages,
  quote_ready: QuoteReadyFooter,
  change_requested: null,
  order_placed: null,
  cancelled: null,
};

export const QuoteSummaryAgentFooter = ({ order }: { order: WindowOrder }) => {
  const Footer = FooterForStatus[order.status];

  return Footer ? <Footer order={order} /> : null;
};
